import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { EditorContent, useEditor, Extension } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import ImageExtension from '@tiptap/extension-image'
import Underline from '@tiptap/extension-underline'
import axios from 'axios'
import { ViewListIcon, LinkIcon, PhotographIcon, MinusIcon, ReplyIcon, XIcon, CheckIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import classNames from '@/utils/classNamesLocal'
import cleanPastedHtml from '@/shared/cleanPastedHtml'

// One editor for every rich-text field on the site. `variant` picks the shape:
//   'textarea'  short field, toolbar still shown, ~50px tall
//   'document'  long-form (project description), taller, images when allowed
// Everything else is opt-in by prop so callers stay one line.

// True when the cursor already has two hard breaks directly behind it.
const isAfterTwoBreaks = ($pos) => {
  const prev = $pos.nodeBefore
  if (prev?.type.name !== 'hardBreak') { return false }
  return $pos.doc.resolve($pos.pos - prev.nodeSize).nodeBefore?.type.name === 'hardBreak'
}

// Enter inserts a line break instead of starting a new paragraph. Tiptap's
// default turns a blank line between paragraphs into an empty <p></p>, which
// has no content and, under our zero-margin .prose rules, no height either,
// so the blank line silently disappears when the field is displayed. A <br>
// is a real line break everywhere: the public project page, the SPA, and the
// mobile app's HTML renderer. Capped at two in a row so a leaned-on Enter key
// can't push the rest of the role off the page.
const BreaksOnEnter = Extension.create({
  name: 'breaksOnEnter',
  priority: 1000,
  addKeyboardShortcuts () {
    const breakOrSwallow = () => {
      const ownsEnter = ['listItem', 'blockquote', 'heading', 'codeBlock']
      if (ownsEnter.some((node) => this.editor.isActive(node))) { return false }
      const { $from, empty } = this.editor.state.selection
      if (empty && isAfterTwoBreaks($from)) { return true }
      return this.editor.commands.setHardBreak()
    }
    return { Enter: breakOrSwallow, 'Shift-Enter': breakOrSwallow, 'Mod-Enter': () => true }
  }
})

const MAX_IMAGE_MB = 10
const MAX_IMAGES_PER_DROP = 5

const ToolButton = ({ onClick, active, label, children, disabled }) => (
  <button type='button' tabIndex='-1' onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={label} aria-label={label} aria-pressed={active} disabled={disabled}
    className={classNames('inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md px-1.5 text-sm transition-colors focus:outline-none',
      disabled ? 'cursor-not-allowed text-gray-300 dark:text-gray-600'
        : active ? 'bg-cccblue/15 text-cccblue' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white')}>
    {children}
  </button>
)

const Divider = () => <span className='mx-0.5 h-5 w-px self-center bg-gray-200 dark:bg-gray-700' aria-hidden='true' />

const LinkPopover = ({ editor, onClose }) => {
  const [url, setUrl] = useState(editor.getAttributes('link').href || '')
  const apply = (e) => {
    e.preventDefault()
    const href = url.trim()
    if (!href) { editor.chain().focus().extendMarkRange('link').unsetLink().run(); onClose(); return }
    const withScheme = /^https?:\/\//i.test(href) ? href : `https://${href}`
    editor.chain().focus().extendMarkRange('link').setLink({ href: withScheme }).run()
    onClose()
  }
  return <form onSubmit={apply} className='flex w-full items-center gap-x-1 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800'>
    <input autoFocus type='text' value={url} onChange={(e) => setUrl(e.target.value)} placeholder='Paste a link, any link' aria-label='Link address' className='!py-1 !text-sm' />
    <button type='submit' title='Apply link' className='rounded-md bg-cccblue p-1.5 text-white hover:bg-cccblue-alt'><CheckIcon className='h-4 w-4' /></button>
    { editor.isActive('link') && <button type='button' onClick={() => { editor.chain().focus().extendMarkRange('link').unsetLink().run(); onClose() }} title='Remove link' className='rounded-md bg-gray-200 p-1.5 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100'><XIcon className='h-4 w-4' /></button> }
    <button type='button' onClick={onClose} className='ml-auto text-xs text-gray-500 hover:text-gray-900 dark:hover:text-white'>Cancel</button>
  </form>
}

const Toolbar = ({ editor, allowImages, onPickImage }) => {
  const [linkOpen, setLinkOpen] = useState(false)
  if (!editor) { return null }
  const run = (fn) => () => fn(editor.chain().focus()).run()

  return <div className='border-b border-gray-200 dark:border-gray-700'>
    <div className='flex flex-wrap items-center gap-0.5 p-1' role='toolbar' aria-label='Text formatting'>
      <ToolButton label='Bold (⌘B)' active={editor.isActive('bold')} onClick={run((c) => c.toggleBold())}><span className='font-bold'>B</span></ToolButton>
      <ToolButton label='Italic (⌘I)' active={editor.isActive('italic')} onClick={run((c) => c.toggleItalic())}><span className='italic'>I</span></ToolButton>
      <ToolButton label='Underline (⌘U)' active={editor.isActive('underline')} onClick={run((c) => c.toggleUnderline())}><span className='underline'>U</span></ToolButton>
      <ToolButton label='Strikethrough' active={editor.isActive('strike')} onClick={run((c) => c.toggleStrike())}><span className='line-through'>S</span></ToolButton>
      <Divider />
      <ToolButton label='Big heading' active={editor.isActive('heading', { level: 2 })} onClick={run((c) => c.toggleHeading({ level: 2 }))}><span className='text-xs font-bold'>H1</span></ToolButton>
      <ToolButton label='Small heading' active={editor.isActive('heading', { level: 3 })} onClick={run((c) => c.toggleHeading({ level: 3 }))}><span className='text-xs font-bold'>H2</span></ToolButton>
      <Divider />
      <ToolButton label='Bullet list' active={editor.isActive('bulletList')} onClick={run((c) => c.toggleBulletList())}><ViewListIcon className='h-4 w-4' /></ToolButton>
      <ToolButton label='Numbered list' active={editor.isActive('orderedList')} onClick={run((c) => c.toggleOrderedList())}><span className='text-xs font-semibold leading-none'>1.<br />2.</span></ToolButton>
      <ToolButton label='Quote' active={editor.isActive('blockquote')} onClick={run((c) => c.toggleBlockquote())}><span className='text-lg leading-none'>&ldquo;</span></ToolButton>
      <ToolButton label='Divider line' onClick={run((c) => c.setHorizontalRule())}><MinusIcon className='h-4 w-4' /></ToolButton>
      <Divider />
      <ToolButton label='Link' active={editor.isActive('link') || linkOpen} onClick={() => setLinkOpen(!linkOpen)}><LinkIcon className='h-4 w-4' /></ToolButton>
      { allowImages && <ToolButton label='Add an image' onClick={onPickImage}><PhotographIcon className='h-4 w-4' /></ToolButton> }
      <span className='ml-auto flex items-center gap-0.5'>
        <ToolButton label='Undo (⌘Z)' disabled={!editor.can().undo()} onClick={run((c) => c.undo())}><ReplyIcon className='h-4 w-4' /></ToolButton>
        <ToolButton label='Redo (⌘⇧Z)' disabled={!editor.can().redo()} onClick={run((c) => c.redo())}><ReplyIcon className='h-4 w-4 -scale-x-100' /></ToolButton>
        <ToolButton label='Clear formatting' onClick={run((c) => c.unsetAllMarks().clearNodes())}><span className='text-xs'>Tx</span></ToolButton>
      </span>
    </div>
    { linkOpen && <LinkPopover editor={editor} onClose={() => setLinkOpen(false)} /> }
  </div>
}

const ProgressBar = ({ progressPercentage }) => (
  <div className='h-1 w-full bg-gray-200 dark:bg-gray-700'>
    <div style={{ width: `${progressPercentage}%` }} className='h-full bg-gradient-to-r from-cccpurple via-cccblue to-cccorange transition-[width]' />
  </div>
)

const RichTextEditor = ({ html, updateHtml, idx, placeholder, variant, onSubmitShortcut, autoFocus, breaksOnEnter, imageUploadSupported, toolbar, className }) => {
  const { getRequest } = useQuery()
  const [progressPercentage, setProgressPercentage] = useState(0)
  const [notice, setNotice] = useState(null)
  const fileInput = React.useRef()
  const allowImages = variant === 'document' && imageUploadSupported

  const uploadImageToR2 = (file) => new Promise((resolve, reject) => {
    const extension = file.name.split('.').pop().toLowerCase() || 'jpg'
    const key = `image-${crypto.randomUUID()}.${extension}`
    const contentType = file.type || 'image/jpeg'
    getRequest('/api/v3/image_uploads/r2_hash', { key, kind: 'Project', content_type: contentType }, async (err, jsonData) => {
      if (err) { reject(err); return }
      try {
        await axios.put(jsonData.url, file, {
          onUploadProgress: (e) => setProgressPercentage((e.loaded / e.total) * 100),
          headers: { 'Content-Type': contentType }
        })
        setProgressPercentage(0)
        resolve(jsonData.image_url + key)
      } catch (uploadError) { setProgressPercentage(0); reject(uploadError) }
    })
  })

  // Shared by paste, drop, and the toolbar button. Returns true when the
  // event was about images and has been dealt with, so ProseMirror stops.
  const insertImages = (view, files, pos) => {
    const images = Array.from(files || []).filter((f) => /image/i.test(f.type))
    if (images.length === 0) { return false }
    if (!imageUploadSupported) { setNotice('Images in descriptions are a Premium perk. Upgrade and I\'ll let you paste all the memes you want.'); return true }
    if (images.length > MAX_IMAGES_PER_DROP) { setNotice(`Whoa, ${MAX_IMAGES_PER_DROP} images at a time please. I only have so many hands.`); return true }
    images.forEach(async (image) => {
      if (image.size / 1024 / 1024 > MAX_IMAGE_MB) { setNotice(`That image is chunky. Keep them under ${MAX_IMAGE_MB}MB and try again.`); return }
      try {
        const src = await uploadImageToR2(image)
        const node = view.state.schema.nodes.image.create({ src })
        const tr = pos == null ? view.state.tr.replaceSelectionWith(node) : view.state.tr.insert(pos, node)
        view.dispatch(tr)
      } catch (e) { setNotice('That image didn\'t make it. Give it another go?') }
    })
    return true
  }

  const extensions = [
    StarterKit,
    Underline,
    Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
    Placeholder.configure({ placeholder }),
    ...(breaksOnEnter ? [BreaksOnEnter] : []),
    ...(allowImages ? [ImageExtension] : [])
  ]

  const editor = useEditor({
    extensions,
    autofocus: autoFocus ? 'end' : false,
    onUpdate ({ editor }) { updateHtml(editor.getHTML()) },
    editorProps: {
      attributes: {
        class: classNames('prose prose-sm max-w-none focus:outline-none dark:prose-invert dark:text-gray-100',
          variant === 'document' ? 'wysiwyg p-3' : 'textarea p-2 text-xs sm:text-sm')
      },
      // The fix for the Word and Google Docs complaints. ProseMirror parses
      // whatever we hand back, so the cleaner runs before the schema sees it.
      transformPastedHTML: (pasted) => cleanPastedHtml(pasted),
      handlePaste: (view, event) => insertImages(view, event.clipboardData?.files),
      handleDrop: (view, event, slice, moved) => {
        if (moved) { return false }
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
        const handled = insertImages(view, event.dataTransfer?.files, coords?.pos)
        if (handled) { event.preventDefault() }
        return handled
      },
      handleDOMEvents: {
        keydown: (view, event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault()
            if (onSubmitShortcut) { onSubmitShortcut() }
            return false
          }
        }
      }
    },
    content: html
  })

  useEffect(() => { if (idx > 1) { editor?.commands.clearContent(true) } }, [idx])
  useEffect(() => {
    if (!notice) { return undefined }
    const t = setTimeout(() => setNotice(null), 5000)
    return () => clearTimeout(t)
  }, [notice])

  const pickImage = () => fileInput.current?.click()
  const onFilePicked = (e) => {
    if (editor) { insertImages(editor.view, e.target.files) }
    e.target.value = ''
  }

  return <div className={classNames('overflow-hidden rounded-md border border-gray-200 bg-white focus-within:border-cccpurple focus-within:ring-1 focus-within:ring-cccpurple dark:border-gray-700 dark:bg-gray-900', className)}>
    { progressPercentage > 0 && <ProgressBar progressPercentage={progressPercentage} /> }
    { toolbar && <Toolbar editor={editor} allowImages={allowImages} onPickImage={pickImage} /> }
    { allowImages && <input ref={fileInput} type='file' accept='image/*' multiple hidden onChange={onFilePicked} /> }
    <EditorContent editor={editor} />
    { notice && <div className='border-t border-cccorange/40 bg-cccorange/10 px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100'>{notice}</div> }
    { onSubmitShortcut && <div className='hidden border-t border-gray-100 px-2 py-1 text-right text-[11px] text-gray-400 dark:border-gray-800 sm:block'><kbd className='rounded bg-gray-100 px-1 font-mono dark:bg-gray-800'>⌘↵</kbd> to send</div> }
  </div>
}

export default RichTextEditor

RichTextEditor.propTypes = {
  html: PropTypes.string,
  updateHtml: PropTypes.func.isRequired,
  idx: PropTypes.number,
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf(['textarea', 'document']),
  onSubmitShortcut: PropTypes.func,
  autoFocus: PropTypes.bool,
  breaksOnEnter: PropTypes.bool,
  imageUploadSupported: PropTypes.bool,
  toolbar: PropTypes.bool,
  className: PropTypes.string
}

RichTextEditor.defaultProps = {
  variant: 'textarea',
  toolbar: true,
  placeholder: 'Type something. Anything. I believe in you.'
}
